import React, { useState, useEffect } from 'react';
import Button from '../shared/Button';
import Modal from '../shared/Modal';
import { useToast } from '../../hooks/useToast';
import {
    getDockTypes,
    createDockTypeConfig,
    updateDockTypeConfig,
    deleteDockTypeConfig
} from '../../services/firestore/systemConfigService';
import { getVehicleTypes } from '../../services/business/vehicleTypeService';
import { getNextSequenceNumber } from '../../services/firestore/sequenceService';

const DockTypeConfig = ({ user }) => {
    const { success, error: toastError } = useToast();
    const [types, setTypes] = useState([]);
    const [vehicleTypes, setVehicleTypes] = useState([]);
    const [loading, setLoading] = useState(false);

    // Modal
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [formData, setFormData] = useState({});
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);

    // Delete Confirmation Modal
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);

    useEffect(() => {
        if (user?.tenantId) {
            loadData();
        }
    }, [user?.tenantId]);

    const loadData = async () => {
        setLoading(true);
        try {
            const [dockTypesData, vehicleTypesList] = await Promise.all([
                getDockTypes(user.tenantId),
                getVehicleTypes(user.tenantId)
            ]);
            setTypes(dockTypesData);
            setVehicleTypes(vehicleTypesList.filter(v => v.isActive));
        } catch (err) {
            toastError('Veriler yüklenemedi');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenModal = (item = null) => {
        if (item) {
            setFormData({
                ...item,
                tenantId: user.tenantId
            });
        } else {
            setFormData({
                tenantId: user.tenantId,
                name: '',
                code: '',
                allowedVehicleTypes: []
            });
        }
        setIsModalOpen(true);
    };

    const handleSave = async () => {
        if (!formData.name) return;

        try {
            let code = formData.code;

            // Auto-generate code using sequence if empty and NEW item
            if (!code && !formData.id) {
                const nextSeq = await getNextSequenceNumber(user.tenantId, 'DOCK_TYPE');
                if (nextSeq) {
                    code = nextSeq;
                } else {
                    code = formData.name.toUpperCase().replace(/ /g, '_').replace(/[^\w-]+/g, '');
                }
            }

            if (formData.id) {
                // UPDATE
                await updateDockTypeConfig(formData.id, {
                    ...formData,
                    code: code || formData.code
                });
                success('Rampa tipi güncellendi');
            } else {
                // CREATE
                await createDockTypeConfig({
                    ...formData,
                    code
                });
                success('Rampa tipi eklendi');
            }

            setIsModalOpen(false);
            loadData();
        } catch (err) {
            toastError('Hata: ' + err.message);
        }
    };

    const confirmDelete = (item) => {
        setItemToDelete(item);
        setIsDeleteModalOpen(true);
    };

    const handleDelete = async () => {
        if (!itemToDelete) return;
        try {
            await deleteDockTypeConfig(itemToDelete.id);
            success('Silindi');
            setIsDeleteModalOpen(false);
            setItemToDelete(null);
            loadData();
        } catch (err) {
            toastError('Silinemedi');
        }
    };

    const toggleAllVehicleTypes = () => {
        const current = formData.allowedVehicleTypes || [];
        const allCodes = vehicleTypes.map(v => v.code);
        const isAllSelected = vehicleTypes.length > 0 && vehicleTypes.every(v => current.includes(v.code));

        if (isAllSelected) {
            setFormData({ ...formData, allowedVehicleTypes: [] });
        } else {
            setFormData({ ...formData, allowedVehicleTypes: allCodes });
        }
    };

    const toggleVehicleType = (vCode) => {
        const current = formData.allowedVehicleTypes || [];
        if (current.includes(vCode)) {
            setFormData({ ...formData, allowedVehicleTypes: current.filter(c => c !== vCode) });
        } else {
            setFormData({ ...formData, allowedVehicleTypes: [...current, vCode] });
        }
    };

    // Helper to view allowed string
    const getAllowedString = (allowedList) => {
        if (!allowedList || allowedList.length === 0) return 'Tüm Araçlar';
        return allowedList.map(c => {
            const vt = vehicleTypes.find(v => v.code === c);
            return vt ? vt.name : c;
        }).join(', ');
    };

    // Pre-populate if empty (First run helper)
    const initDefaults = async () => {
        if (types.length > 0) return;
        const defaults = [
            { name: 'Genel', code: 'general' },
            { name: 'Soğuk Hava', code: 'cold' },
            { name: 'Kuru Yük', code: 'dry' },
            { name: 'Express', code: 'express' }
        ];
        try {
            for (const d of defaults) {
                await createDockTypeConfig({ ...d, allowedVehicleTypes: [], tenantId: user.tenantId });
            }
            loadData();
        } catch (e) { console.error(e); }
    };

    return (
        <div>
            <div className="flex justify-between items-center mb-md">
                <Button variant="secondary" size="sm" onClick={initDefaults} disabled={types.length > 0}>Varsayılanları Yükle</Button>
                <Button variant="primary" onClick={() => handleOpenModal()}>+ Yeni Rampa Tipi</Button>
            </div>

            <div className="card" style={{ padding: 0 }}>
                {loading ? (
                    <div className="p-lg text-center">Yükleniyor...</div>
                ) : types.length === 0 ? (
                    <div className="p-lg text-center text-secondary">Kayıt yok.</div>
                ) : (
                    <table className="table">
                        <thead>
                            <tr>
                                <th>Adı</th>
                                <th>Kod</th>
                                <th>İzin Verilen Araçlar</th>
                                <th>İşlemler</th>
                            </tr>
                        </thead>
                        <tbody>
                            {types.map(t => (
                                <tr key={t.id}>
                                    <td style={{ fontWeight: 600 }}>{t.name}</td>
                                    <td className="text-secondary">{t.code}</td>
                                    <td className="text-sm text-secondary">{getAllowedString(t.allowedVehicleTypes)}</td>
                                    <td>
                                        <div className="flex gap-xs">
                                            <Button size="sm" variant="secondary" onClick={() => handleOpenModal(t)}>Düzenle</Button>
                                            <Button size="sm" variant="danger" onClick={() => confirmDelete(t)}>Sil</Button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={formData.id ? "Rampa Tipini Düzenle" : "Yeni Rampa Tipi"}>
                <div className="form-group">
                    <label className="form-label">Tip Adı</label>
                    <input
                        className="input"
                        value={formData.name || ''}
                        onChange={e => setFormData({ ...formData, name: e.target.value })}
                        placeholder="Örn: Yanıcı Madde"
                    />
                </div>
                <div className="form-group">
                    <label className="form-label">Kod (Opsiyonel)</label>
                    <input
                        className="input"
                        value={formData.code || ''}
                        onChange={e => setFormData({ ...formData, code: e.target.value })}
                        placeholder="Boş bırakılırsa 'DOCK_TYPE' numara aralığından veya isimden üretilir."
                    />
                </div>

                <div className="form-group" style={{ position: 'relative' }}>
                    <label className="form-label mb-sm">İzin Verilen Araç Tipleri <span className="text-secondary text-xs font-normal ml-xs">(Çoklu Seçim)</span></label>

                    {/* Premium Multi-Select Trigger */}
                    <div
                        className="input flex items-center justify-between cursor-pointer hover:border-primary transition-colors"
                        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                        style={{
                            minHeight: '46px',
                            height: 'auto',
                            padding: '8px 12px',
                            background: isDropdownOpen ? 'var(--bg-hover)' : 'var(--bg-input)',
                            borderColor: isDropdownOpen ? 'var(--primary-color)' : 'var(--border-color)'
                        }}
                    >
                        <div className="flex flex-wrap gap-xs flex-1">
                            {(!formData.allowedVehicleTypes || formData.allowedVehicleTypes.length === 0) ? (
                                <span className="text-secondary select-none">Tümü (Kısıtlama Yok)</span>
                            ) : formData.allowedVehicleTypes.length > 3 ? (
                                <span className="font-medium text-primary select-none">
                                    {formData.allowedVehicleTypes.length} Araç Tipi Seçili
                                </span>
                            ) : (
                                formData.allowedVehicleTypes.map(code => {
                                    const vt = vehicleTypes.find(v => v.code === code);
                                    return (
                                        <span key={code} className="badge badge-neutral text-xs flex items-center gap-xs"
                                            style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.1)' }}>
                                            {vt ? `${vt.code} - ${vt.name}` : code}
                                        </span>
                                    );
                                })
                            )}
                        </div>

                        <div style={{ transform: isDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s', marginLeft: '8px' }}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-secondary">
                                <polyline points="6 9 12 15 18 9"></polyline>
                            </svg>
                        </div>
                    </div>

                    {/* Premium Dropdown Content */}
                    {isDropdownOpen && (
                        <>
                            <div
                                style={{ position: 'fixed', inset: 0, zIndex: 999 }}
                                onClick={() => setIsDropdownOpen(false)}
                            />

                            <div className="card p-0" style={{
                                position: 'absolute',
                                top: 'calc(100% + 8px)',
                                left: 0,
                                right: 0,
                                zIndex: 1000,
                                maxHeight: '300px',
                                overflowY: 'auto',
                                background: 'var(--bg-card)',
                                border: '1px solid var(--border-color)',
                                boxShadow: 'var(--shadow-md)',
                                borderRadius: '12px',
                                animation: 'fadeIn 0.2s ease-out'
                            }}>
                                {/* Header / Select All */}
                                <div
                                    className="p-md cursor-pointer flex items-center justify-between hover:bg-bg-hover transition-colors sticky top-0 z-10"
                                    onClick={toggleAllVehicleTypes}
                                    style={{ background: 'var(--bg-card)', borderBottom: '1px solid var(--border-color)' }}
                                >
                                    <span className="font-bold text-sm" style={{ color: 'var(--text-primary)' }}>Tümünü Seç / Temizle</span>
                                    <div style={{
                                        width: '20px', height: '20px', borderRadius: '6px',
                                        border: '2px solid var(--border-color)',
                                        background: (vehicleTypes.length > 0 && vehicleTypes.every(vt => (formData.allowedVehicleTypes || []).includes(vt.code))) ? 'var(--primary-color)' : 'transparent',
                                        borderColor: (vehicleTypes.length > 0 && vehicleTypes.every(vt => (formData.allowedVehicleTypes || []).includes(vt.code))) ? 'var(--primary-color)' : 'var(--border-color)',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        transition: 'all 0.2s'
                                    }}>
                                        {(vehicleTypes.length > 0 && vehicleTypes.every(vt => (formData.allowedVehicleTypes || []).includes(vt.code))) && (
                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round">
                                                <polyline points="20 6 9 17 4 12"></polyline>
                                            </svg>
                                        )}
                                    </div>
                                </div>

                                {/* List Options */}
                                <div className="p-xs">
                                    {vehicleTypes.map(vt => {
                                        const isSelected = (formData.allowedVehicleTypes || []).includes(vt.code);
                                        return (
                                            <div
                                                key={vt.id}
                                                className="p-sm rounded cursor-pointer flex items-center justify-between hover:bg-bg-hover transition-all mb-xs"
                                                onClick={() => toggleVehicleType(vt.code)}
                                                style={{
                                                    background: isSelected ? 'var(--bg-hover)' : 'transparent',
                                                    border: isSelected ? '1px solid var(--primary-color)' : '1px solid transparent'
                                                }}
                                            >
                                                <div className="flex items-center">
                                                    <span className="text-sm font-medium font-mono mr-sm" style={{ color: isSelected ? 'var(--primary-color)' : 'var(--text-secondary)', opacity: 0.8 }}>{vt.code}</span>
                                                    <span className="text-sm font-bold" style={{ color: isSelected ? 'var(--primary-color)' : 'var(--text-primary)' }}>- {vt.name}</span>
                                                </div>

                                                <div style={{
                                                    width: '20px', height: '20px', borderRadius: '50%',
                                                    border: '2px solid',
                                                    borderColor: isSelected ? 'var(--primary-color)' : 'var(--border-color)',
                                                    background: isSelected ? 'var(--primary-color)' : 'transparent',
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    transition: 'all 0.2s'
                                                }}>
                                                    {isSelected && (
                                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round">
                                                            <polyline points="20 6 9 17 4 12"></polyline>
                                                        </svg>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </>
                    )}

                    <div className="text-secondary text-xs mt-sm flex items-center gap-xs">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="10"></circle>
                            <line x1="12" y1="16" x2="12" y2="12"></line>
                            <line x1="12" y1="8" x2="12.01" y2="8"></line>
                        </svg>
                        <span>Hiçbiri seçilmezse tüm araç tiplerine izin verilir.</span>
                    </div>
                </div>

                <div className="flex justify-end gap-md mt-lg">
                    <Button variant="secondary" onClick={() => setIsModalOpen(false)}>İptal</Button>
                    <Button variant="primary" onClick={handleSave}>Kaydet</Button>
                </div>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                title="Silme İşlemi"
                size="sm"
            >
                <div style={{ padding: '20px', textAlign: 'center' }}>
                    <div style={{ fontSize: '3rem', marginBottom: '16px' }}>⚠️</div>
                    <p style={{ color: 'white', fontSize: '1.1rem', marginBottom: '8px' }}>
                        <b>{itemToDelete?.name}</b> rampa tipini silmek istediğinize emin misiniz?
                    </p>
                    <p className="text-secondary text-sm">Bu işlem geri alınamaz.</p>

                    <div className="flex justify-center gap-md mt-xl">
                        <Button variant="secondary" onClick={() => setIsDeleteModalOpen(false)}>Vazgeç</Button>
                        <Button variant="danger" onClick={handleDelete}>Evet, Sil</Button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default DockTypeConfig;

